import React from 'react';

import {ChartConfiguration} from 'chart.js';
import {useTranslation} from 'react-i18next';

import {AuditStatisticsEntity} from '@entities/audit-statistics/entity';
import {ViolationDictionaryItem} from '@entities/violations/types';

import {
    ChartTitle,
    DonutChartBox,
    DonutContainer,
    DonutLegend,
    DonutLegendRow,
    DonutPercent,
    DonutValue,
    LegendDot,
} from '../styled';

import {
    createChart,
    getLocalizedTitle,
    VIOLATION_TYPES_BASE_COLORS,
    VIOLATION_TYPES_COLORS,
} from './chartUtils';

interface Props {
    statistics: AuditStatisticsEntity;
    violationTypes: ViolationDictionaryItem[];
}

export const ViolationsByTypeChart = ({statistics, violationTypes}: Props) => {
    const {t, i18n} = useTranslation();
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const violationTypeCodes = React.useMemo(() => {
        const keys = Object.keys(statistics.violationsByType);
        const sortedCodes = violationTypes
            .filter((item) => keys.includes(item.code))
            .sort((a, b) =>
                getLocalizedTitle(a, i18n.language).localeCompare(
                    getLocalizedTitle(b, i18n.language),
                ),
            )
            .map((item) => item.code);

        return keys.sort((a, b) => sortedCodes.indexOf(a) - sortedCodes.indexOf(b));
    }, [i18n.language, statistics.violationsByType, violationTypes]);

    const chartColors = React.useMemo(() => {
        const colors = [...VIOLATION_TYPES_COLORS];
        return violationTypeCodes.map(
            (code) => VIOLATION_TYPES_BASE_COLORS[code] ?? colors.shift(),
        );
    }, [violationTypeCodes]);

    const labels = violationTypeCodes.map(
        (code) =>
            getLocalizedTitle(
                violationTypes.find((item) => item.code === code),
                i18n.language,
            ) || code,
    );

    React.useEffect(() => {
        if (!canvasRef.current) return undefined;

        const config: ChartConfiguration<'doughnut'> = {
            type: 'doughnut',
            data: {
                datasets: [
                    {
                        data: violationTypeCodes.map((code) =>
                            statistics.getViolationsByTypeNumValue(code),
                        ),
                        backgroundColor: chartColors.map((item) => item?.borderColor ?? '#4F79D0'),
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '80%',
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        padding: 16,
                        callbacks: {
                            label: () => '',
                            beforeLabel: (item) =>
                                `${labels[item.dataIndex]}: ${item.formattedValue}`,
                        },
                    },
                },
            },
        };

        const chart = createChart(canvasRef.current, config);
        return () => chart.destroy();
    }, [chartColors, labels, statistics, violationTypeCodes]);

    return (
        <>
            <ChartTitle style={{paddingBottom: 16, marginBottom: 48}}>
                {t('page_audit.page_statistic.violations-by-type-chart-title')}
            </ChartTitle>
            <DonutContainer>
                <DonutLegend>
                    <DonutLegendRow>
                        <span />
                        <span>{t('page_audit.page_statistic.violations-by-type-chart-total')}</span>
                        <DonutValue>{statistics.violationsByTypeTotal}</DonutValue>
                        <DonutPercent />
                    </DonutLegendRow>
                    {violationTypeCodes.map((code, index) => (
                        <DonutLegendRow key={code}>
                            <LegendDot color={chartColors[index]?.borderColor ?? '#4F79D0'} />
                            <span>{labels[index]}</span>
                            <DonutValue>{statistics.getViolationsByTypeNumValue(code)}</DonutValue>
                            <DonutPercent>
                                {statistics.getViolationsByTypePercentValue(code)}%
                            </DonutPercent>
                        </DonutLegendRow>
                    ))}
                </DonutLegend>
                <DonutChartBox>
                    <canvas ref={canvasRef} />
                </DonutChartBox>
            </DonutContainer>
        </>
    );
};
